export type UserRole = 'admin' | 'doctor' | 'volunteer' | 'member' | 'student' | 'patient';

export interface RoleConfig {
  role: UserRole;
  label: string;
  dashboardPath: string;
  initials: string;
  color: string;
  navGroups: RoleNavGroup[];
}

export interface RoleNavGroup {
  id: string;
  label: string;
  items: RoleNavItem[];
}

export interface RoleNavItem {
  id: string;
  label: string;
  href: string;
  iconKey: string;
  badge?: number;
  external?: boolean;
}

// Demo credentials → role mapping
export const demoRoleMap: Record<string, UserRole> = {
  'admin@srisevatrust.in': 'admin',
  'dr.meena@srisevatrust.in': 'doctor',
  'ravi.volunteer@srisevatrust.in': 'volunteer',
  'priya.member@srisevatrust.in': 'member',
  'student@srisevatrust.in': 'student',
  'patient@srisevatrust.in': 'patient',
};

export const demoUserNames: Record<string, string> = {
  'admin@srisevatrust.in': 'Super Admin',
  'dr.meena@srisevatrust.in': 'Dr. Meena',
  'ravi.volunteer@srisevatrust.in': 'Ravi Kumar',
  'priya.member@srisevatrust.in': 'Priya Sharma',
  'student@srisevatrust.in': 'Arjun Student',
  'patient@srisevatrust.in': 'Sunita Patient',
};

export const roleInitials: Record<UserRole, string> = {
  admin: 'SA',
  doctor: 'DM',
  volunteer: 'RK',
  member: 'PS',
  student: 'AS',
  patient: 'SP',
};

export const roleColors: Record<UserRole, string> = {
  admin: 'gradient-saffron',
  doctor: 'bg-blue-500',
  volunteer: 'bg-green-500',
  member: 'bg-purple-500',
  student: 'bg-indigo-500',
  patient: 'bg-teal-500',
};

export const roleDashboardPaths: Record<UserRole, string> = {
  admin: '/admin-dashboard',
  doctor: '/doctor-dashboard',
  volunteer: '/volunteer-dashboard',
  member: '/member-dashboard',
  student: '/student-dashboard',
  patient: '/patient-dashboard',
};

// Admin nav groups (full access)
export const adminNavGroups: RoleNavGroup[] = [
  {
    id: 'group-main',
    label: 'Main',
    items: [
      { id: 'nav-dashboard', label: 'Dashboard', href: '/admin-dashboard', iconKey: 'home' },
      { id: 'nav-website', label: 'Main Website', href: 'https://srisevatrust.in/', iconKey: 'globe', external: true },
    ],
  },
  {
    id: 'group-website',
    label: 'Website',
    items: [
      { id: 'nav-cms', label: 'CMS & Website', href: '/cms', iconKey: 'globe' },
    ],
  },
  {
    id: 'group-access',
    label: 'User & Access',
    items: [
      { id: 'nav-users', label: 'Users', href: '/users', iconKey: 'users', badge: 3 },
      { id: 'nav-roles', label: 'Roles & Permissions', href: '/roles', iconKey: 'clipboard' },
    ],
  },
  {
    id: 'group-fundraising',
    label: 'Fundraising',
    items: [
      { id: 'nav-donations', label: 'Donations', href: '/donation-management', iconKey: 'currency', badge: 7 },
      { id: 'nav-members', label: 'Members', href: '/members', iconKey: 'usercheck' },
      { id: 'nav-payments', label: 'Payments & Razorpay', href: '/payments', iconKey: 'currency' },
    ],
  },
  {
    id: 'group-volunteers',
    label: 'Volunteers',
    items: [
      { id: 'nav-volunteers', label: 'Volunteers', href: '/volunteers', iconKey: 'hand', badge: 4 },
    ],
  },
  {
    id: 'group-healthcare',
    label: 'Healthcare',
    items: [
      { id: 'nav-doctors', label: 'Doctors', href: '/doctors', iconKey: 'heart' },
      { id: 'nav-patients', label: 'Patients', href: '/patients', iconKey: 'users' },
      { id: 'nav-consultations', label: 'Consultations', href: '/consultations', iconKey: 'calendar' },
    ],
  },
  {
    id: 'group-education',
    label: 'Education',
    items: [
      { id: 'nav-courses', label: 'Courses', href: '/courses', iconKey: 'book' },
      { id: 'nav-students', label: 'Students', href: '/students', iconKey: 'users' },
      { id: 'nav-teachers', label: 'Teachers', href: '/teachers', iconKey: 'usercheck' },
    ],
  },
  {
    id: 'group-events',
    label: 'Events',
    items: [
      { id: 'nav-events', label: 'Events', href: '/events', iconKey: 'calendar' },
    ],
  },
  {
    id: 'group-content',
    label: 'Content',
    items: [
      { id: 'nav-blogs', label: 'Blogs', href: '/blogs', iconKey: 'document' },
      { id: 'nav-gallery', label: 'Gallery & Media', href: '/gallery', iconKey: 'photo' },
      { id: 'nav-news', label: 'News', href: '/news', iconKey: 'document' },
    ],
  },
  {
    id: 'group-documents',
    label: 'Documents',
    items: [
      { id: 'nav-certificates', label: 'Certificates & Receipts', href: '/certificates', iconKey: 'document' },
    ],
  },
  {
    id: 'group-comms',
    label: 'Communication',
    items: [
      { id: 'nav-emails', label: 'Email Engine', href: '/emails', iconKey: 'envelope', badge: 2 },
      { id: 'nav-notifications', label: 'Notifications', href: '/notifications', iconKey: 'envelope' },
    ],
  },
  {
    id: 'group-analytics',
    label: 'Analytics',
    items: [
      { id: 'nav-reports', label: 'Reports & Audit Logs', href: '/reports', iconKey: 'chart' },
    ],
  },
  {
    id: 'group-tools',
    label: 'Tools',
    items: [
      { id: 'nav-ai', label: 'AI Assistant', href: '/ai-assistant', iconKey: 'sparkles' },
      { id: 'nav-api-docs', label: 'API Documentation', href: '/api-docs', iconKey: 'server' },
      { id: 'nav-config', label: 'Configuration', href: '/config', iconKey: 'server' },
      { id: 'nav-settings', label: 'Admin Settings', href: '/settings', iconKey: 'cog' },
    ],
  },
];

// Doctor nav groups
export const doctorNavGroups: RoleNavGroup[] = [
  {
    id: 'group-main',
    label: 'Main',
    items: [
      { id: 'nav-dashboard', label: 'My Dashboard', href: '/doctor-dashboard', iconKey: 'home' },
    ],
  },
  {
    id: 'group-healthcare',
    label: 'Healthcare',
    items: [
      { id: 'nav-consultations', label: 'My Consultations', href: '/consultations', iconKey: 'calendar' },
      { id: 'nav-patients', label: 'My Patients', href: '/patients', iconKey: 'users' },
    ],
  },
  {
    id: 'group-schedule',
    label: 'Schedule',
    items: [
      { id: 'nav-events', label: 'Medical Events', href: '/events', iconKey: 'calendar' },
    ],
  },
  {
    id: 'group-account',
    label: 'Account',
    items: [
      { id: 'nav-notifications', label: 'Notifications', href: '/notifications', iconKey: 'envelope' },
      { id: 'nav-settings', label: 'Doctor Settings', href: '/settings', iconKey: 'cog' },
    ],
  },
];

// Volunteer nav groups
export const volunteerNavGroups: RoleNavGroup[] = [
  {
    id: 'group-main',
    label: 'Main',
    items: [
      { id: 'nav-dashboard', label: 'My Dashboard', href: '/volunteer-dashboard', iconKey: 'home' },
    ],
  },
  {
    id: 'group-work',
    label: 'My Work',
    items: [
      { id: 'nav-volunteers', label: 'Volunteer Hub', href: '/volunteers', iconKey: 'hand' },
      { id: 'nav-events', label: 'Events & Programs', href: '/events', iconKey: 'calendar' },
      { id: 'nav-courses', label: 'Training Courses', href: '/courses', iconKey: 'book' },
    ],
  },
  {
    id: 'group-docs',
    label: 'Documents',
    items: [
      { id: 'nav-certificates', label: 'My Certificates', href: '/certificates', iconKey: 'document' },
    ],
  },
  {
    id: 'group-account',
    label: 'Account',
    items: [
      { id: 'nav-notifications', label: 'Notifications', href: '/notifications', iconKey: 'envelope' },
      { id: 'nav-settings', label: 'Volunteer Settings', href: '/settings', iconKey: 'cog' },
    ],
  },
];

// Member nav groups
export const memberNavGroups: RoleNavGroup[] = [
  {
    id: 'group-main',
    label: 'Main',
    items: [
      { id: 'nav-dashboard', label: 'My Dashboard', href: '/member-dashboard', iconKey: 'home' },
    ],
  },
  {
    id: 'group-membership',
    label: 'Membership',
    items: [
      { id: 'nav-members', label: 'My Membership', href: '/members', iconKey: 'usercheck' },
      { id: 'nav-donations', label: 'My Donations', href: '/donation-management', iconKey: 'currency' },
    ],
  },
  {
    id: 'group-programs',
    label: 'Programs',
    items: [
      { id: 'nav-events', label: 'Events', href: '/events', iconKey: 'calendar' },
      { id: 'nav-courses', label: 'Courses', href: '/courses', iconKey: 'book' },
    ],
  },
  {
    id: 'group-docs',
    label: 'Documents',
    items: [
      { id: 'nav-certificates', label: 'My Certificates', href: '/certificates', iconKey: 'document' },
      { id: 'nav-receipts', label: 'Receipts', href: '/receipts', iconKey: 'clipboard' },
    ],
  },
  {
    id: 'group-account',
    label: 'Account',
    items: [
      { id: 'nav-notifications', label: 'Notifications', href: '/notifications', iconKey: 'envelope' },
      { id: 'nav-settings', label: 'Member Settings', href: '/settings', iconKey: 'cog' },
    ],
  },
];

// Student nav groups
export const studentNavGroups: RoleNavGroup[] = [
  {
    id: 'group-main',
    label: 'Main',
    items: [
      { id: 'nav-dashboard', label: 'My Dashboard', href: '/student-dashboard', iconKey: 'home' },
    ],
  },
  {
    id: 'group-learning',
    label: 'Learning',
    items: [
      { id: 'nav-courses', label: 'My Courses', href: '/courses', iconKey: 'book' },
      { id: 'nav-events', label: 'Events & Workshops', href: '/events', iconKey: 'calendar' },
    ],
  },
  {
    id: 'group-docs',
    label: 'Documents',
    items: [
      { id: 'nav-certificates', label: 'My Certificates', href: '/certificates', iconKey: 'document' },
    ],
  },
  {
    id: 'group-account',
    label: 'Account',
    items: [
      { id: 'nav-notifications', label: 'Notifications', href: '/notifications', iconKey: 'envelope' },
      { id: 'nav-settings', label: 'Student Settings', href: '/settings', iconKey: 'cog' },
    ],
  },
];

// Patient nav groups
export const patientNavGroups: RoleNavGroup[] = [
  {
    id: 'group-main',
    label: 'Main',
    items: [
      { id: 'nav-dashboard', label: 'My Dashboard', href: '/patient-dashboard', iconKey: 'home' },
    ],
  },
  {
    id: 'group-healthcare',
    label: 'Healthcare',
    items: [
      { id: 'nav-consultations', label: 'My Consultations', href: '/consultations', iconKey: 'calendar' },
      { id: 'nav-doctors', label: 'Find a Doctor', href: '/doctors', iconKey: 'heart' },
    ],
  },
  {
    id: 'group-account',
    label: 'Account',
    items: [
      { id: 'nav-notifications', label: 'Notifications', href: '/notifications', iconKey: 'envelope' },
      { id: 'nav-settings', label: 'Patient Settings', href: '/settings', iconKey: 'cog' },
    ],
  },
];

export const navGroupsByRole: Record<UserRole, RoleNavGroup[]> = {
  admin: adminNavGroups,
  doctor: doctorNavGroups,
  volunteer: volunteerNavGroups,
  member: memberNavGroups,
  student: studentNavGroups,
  patient: patientNavGroups,
};
